/* ==========================================================================
   McCalls — Product media gallery
   Rail / stacked layouts, mobile snap carousel, cursor zoom, fullscreen
   lightbox with pinch-zoom, hosted/external video, 3D models, variant →
   media sync and colour grouping. No dependencies.
   Markup: snippets/product-media-gallery.liquid · Styles: assets/product-gallery.css
   ========================================================================== */

(function () {
  'use strict';

  var mqDesktop = window.matchMedia('(min-width: 769px)');
  var mqFine = window.matchMedia('(hover: hover) and (pointer: fine)');
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var instances = new WeakMap();
  var HINT_KEY = 'pmg-hint-seen';

  /* ── Small helpers ──────────────────────────────────────────────────── */

  function qs(root, sel) { return root ? root.querySelector(sel) : null; }
  function qsa(root, sel) { return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : []; }
  function isMobile() { return !mqDesktop.matches; }
  function isFine() { return mqFine.matches; }
  function reduceMotion() { return mqReduce.matches; }
  function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

  function handleize(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function openDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      try { dialog.showModal(); } catch (_) { dialog.setAttribute('open', ''); }
    } else {
      dialog.setAttribute('open', '');
    }
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') {
      try { dialog.close(); } catch (_) { dialog.removeAttribute('open'); }
    } else {
      dialog.removeAttribute('open');
      dialog.dispatchEvent(new Event('close'));
    }
  }

  function rafThrottle(fn) {
    var ticking = false;
    return function () {
      var args = arguments, ctx = this;
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        fn.apply(ctx, args);
      });
    };
  }

  function idle(fn, timeout) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(fn, { timeout: timeout || 1500 });
    } else {
      window.setTimeout(fn, 200);
    }
  }

  /** Scroll a child into view *inside* its scroll container only (never the page). */
  function scrollIntoContainer(container, el, behavior) {
    if (!container || !el) return;
    var opts = { behavior: behavior || (reduceMotion() ? 'auto' : 'smooth') };
    var top = el.offsetTop, left = el.offsetLeft;
    var bottom = top + el.offsetHeight, right = left + el.offsetWidth;
    var changed = false;
    if (top < container.scrollTop) { opts.top = top; changed = true; }
    else if (bottom > container.scrollTop + container.clientHeight) { opts.top = bottom - container.clientHeight; changed = true; }
    if (left < container.scrollLeft) { opts.left = left; changed = true; }
    else if (right > container.scrollLeft + container.clientWidth) { opts.left = right - container.clientWidth; changed = true; }
    if (changed) {
      try { container.scrollTo(opts); } catch (_) {
        if (opts.top != null) container.scrollTop = opts.top;
        if (opts.left != null) container.scrollLeft = opts.left;
      }
    }
  }

  /** Index (into `slides`) of the slide nearest the scroll position of a horizontal track. */
  function nearestSlideIndex(track, slides) {
    var best = -1, bestDist = Infinity, x = track.scrollLeft;
    slides.forEach(function (s, i) {
      if (s.hidden) return;
      var d = Math.abs(s.offsetLeft - x);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    return best;
  }

  /* ── Tablist (thumbnail rails, main + lightbox) ─────────────────────── */

  function createTablist(container, onSelect) {
    if (!container) return null;
    var tabs = qsa(container, '[role="tab"]');
    var horizontal = container.getAttribute('aria-orientation') === 'horizontal';

    function visibleTabs() { return tabs.filter(function (t) { return !t.hidden; }); }
    function indexOf(tab) { return parseInt(tab.getAttribute('data-index'), 10); }

    function setActive(index, behavior) {
      tabs.forEach(function (t) {
        var sel = indexOf(t) === index;
        t.setAttribute('aria-selected', sel ? 'true' : 'false');
        t.tabIndex = sel ? 0 : -1;
        if (sel) scrollIntoContainer(container, t, behavior);
      });
    }

    function onClick(e) {
      var tab = e.target.closest('[role="tab"]');
      if (!tab || !container.contains(tab)) return;
      onSelect(indexOf(tab), 'thumb');
    }

    function onKeydown(e) {
      var tab = e.target.closest('[role="tab"]');
      if (!tab) return;
      var vis = visibleTabs();
      var pos = vis.indexOf(tab);
      if (pos < 0) return;
      var next = null;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = vis[(pos + 1) % vis.length]; break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = vis[(pos - 1 + vis.length) % vis.length]; break;
        case 'Home':
          next = vis[0]; break;
        case 'End':
          next = vis[vis.length - 1]; break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          onSelect(indexOf(tab), 'thumb');
          return;
        default:
          return;
      }
      if (next) {
        e.preventDefault();
        next.focus();
        onSelect(indexOf(next), 'thumb');
      }
    }

    container.addEventListener('click', onClick);
    container.addEventListener('keydown', onKeydown);

    return {
      tabs: tabs,
      setActive: setActive,
      horizontal: horizontal,
      destroy: function () {
        container.removeEventListener('click', onClick);
        container.removeEventListener('keydown', onKeydown);
      }
    };
  }

  /* ── Cursor zoom (desktop, in place) ─────────────────────────────────── */

  function createZoom(gallery) {
    var root = gallery.root;
    var mode = root.getAttribute('data-zoom') || 'click';
    var cache = {};
    var activeSlide = null;
    var pending = 0;

    function layerFor(slide) {
      var media = qs(slide, '.pmg__media');
      if (!media) return null;
      var layer = qs(media, '.pmg__zoom');
      if (!layer) {
        layer = document.createElement('div');
        layer.className = 'pmg__zoom';
        layer.setAttribute('aria-hidden', 'true');
        media.appendChild(layer);
      }
      return layer;
    }

    function warm(slide) {
      var img = qs(slide, 'img[data-zoom-src]');
      if (!img) return Promise.reject();
      var src = img.getAttribute('data-zoom-src');
      if (!cache[src]) {
        cache[src] = new Promise(function (resolve, reject) {
          var pre = new Image();
          pre.onload = function () { resolve(src); };
          pre.onerror = function () { delete cache[src]; reject(); };
          pre.src = src;
        });
      }
      return cache[src];
    }

    function position(slide, e) {
      var layer = qs(slide, '.pmg__zoom');
      if (!layer) return;
      var rect = layer.getBoundingClientRect();
      var x = clamp((e.clientX - rect.left) / rect.width, 0, 1) * 100;
      var y = clamp((e.clientY - rect.top) / rect.height, 0, 1) * 100;
      layer.style.backgroundPosition = x + '% ' + y + '%';
    }

    var move = rafThrottle(function (slide, e) { position(slide, e); });

    function activate(slide, e) {
      if (!slide || slide.getAttribute('data-media-type') !== 'image') return;
      var token = ++pending;
      var layer = layerFor(slide);
      if (!layer) return;
      slide.classList.add('is-zoom-loading');
      warm(slide).then(function (src) {
        slide.classList.remove('is-zoom-loading');
        if (token !== pending) return;
        layer.style.backgroundImage = 'url("' + src + '")';
        if (e) position(slide, e);
        slide.classList.add('is-zoomed');
        activeSlide = slide;
        gallery.dismissHint();
      }).catch(function () {
        slide.classList.remove('is-zoom-loading');
      });
    }

    function deactivate() {
      pending++;
      if (activeSlide) {
        activeSlide.classList.remove('is-zoomed', 'is-zoom-loading');
        activeSlide = null;
      }
    }

    function slideFromEvent(e) {
      var slide = e.target.closest('[data-pmg-slide]');
      if (!slide || slide.getAttribute('data-media-type') !== 'image') return null;
      if (!e.target.closest('.pmg__media')) return null;
      return slide;
    }

    function onPointerEnter(e) {
      if (!isFine() || isMobile()) return;
      var slide = slideFromEvent(e);
      if (!slide) return;
      warm(slide).catch(function () {});
      if (mode === 'hover') activate(slide, e);
    }

    function onPointerMove(e) {
      if (!activeSlide) return;
      if (!activeSlide.contains(e.target)) { deactivate(); return; }
      move(activeSlide, e);
    }

    function onPointerLeave(e) {
      var slide = slideFromEvent(e);
      if (slide && slide === activeSlide) deactivate();
    }

    function onClick(e) {
      if (!isFine() || isMobile()) return;
      var slide = slideFromEvent(e);
      if (!slide) return;
      if (mode === 'click') {
        if (!qs(slide, 'img[data-zoom-src]')) {
          // Source too small to zoom crisply — open the lightbox instead
          if (gallery.lightbox) gallery.lightbox.open(gallery.slides.indexOf(slide), gallery.track);
          return;
        }
        if (activeSlide === slide) deactivate();
        else { deactivate(); activate(slide, e); }
      }
    }

    // pointerenter/leave don't bubble — bind with capture on the track.
    var track = gallery.track;
    track.addEventListener('pointerenter', onPointerEnter, true);
    track.addEventListener('pointerleave', onPointerLeave, true);
    track.addEventListener('pointermove', onPointerMove);
    if (mode === 'click') track.addEventListener('click', onClick);

    return {
      mode: mode,
      warm: warm,
      deactivate: deactivate,
      isActive: function () { return !!activeSlide; },
      destroy: function () {
        track.removeEventListener('pointerenter', onPointerEnter, true);
        track.removeEventListener('pointerleave', onPointerLeave, true);
        track.removeEventListener('pointermove', onPointerMove);
        track.removeEventListener('click', onClick);
      }
    };
  }

  /* ── Pinch / pan / click zoom (lightbox) ─────────────────────────────── */

  function createPinch(container, img, opts) {
    opts = opts || {};
    var MAX = 4, TAP_SCALE = 2.5;
    var scale = 1, tx = 0, ty = 0;
    var pointers = new Map();
    var start = null;
    var moved = false;
    var lastTap = 0;
    var raf = null;
    var downOnImg = false; // pointer capture retargets events, so remember the real down target

    function bounds() {
      var cw = container.clientWidth, ch = container.clientHeight;
      var w = img.clientWidth * scale, h = img.clientHeight * scale;
      var ox = img.offsetLeft, oy = img.offsetTop;
      var res = {};
      if (w <= cw) { res.minX = res.maxX = (cw - w) / 2 - ox; }
      else { res.maxX = -ox; res.minX = cw - ox - w; }
      if (h <= ch) { res.minY = res.maxY = (ch - h) / 2 - oy; }
      else { res.maxY = -oy; res.minY = ch - oy - h; }
      return res;
    }

    function apply() {
      if (raf) return;
      raf = window.requestAnimationFrame(function () {
        raf = null;
        img.style.transform = scale === 1 && tx === 0 && ty === 0
          ? ''
          : 'translate(' + tx + 'px, ' + ty + 'px) scale(' + scale + ')';
        var zoomed = scale > 1.001;
        container.classList.toggle('is-zoomed', zoomed);
        if (opts.onZoomChange) opts.onZoomChange(zoomed);
      });
    }

    function constrain() {
      var b = bounds();
      tx = clamp(tx, b.minX, b.maxX);
      ty = clamp(ty, b.minY, b.maxY);
    }

    function zoomAt(newScale, cx, cy) {
      newScale = clamp(newScale, 1, MAX);
      if (newScale === 1) { scale = 1; tx = 0; ty = 0; apply(); return; }
      var ox = img.offsetLeft, oy = img.offsetTop;
      var px = (cx - ox - tx) / scale;
      var py = (cy - oy - ty) / scale;
      scale = newScale;
      tx = cx - ox - px * scale;
      ty = cy - oy - py * scale;
      constrain();
      apply();
    }

    function local(e) {
      var r = container.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    function reset() {
      scale = 1; tx = 0; ty = 0; pointers.clear(); start = null;
      container.classList.remove('is-panning');
      apply();
    }

    function toggleAt(pt) {
      if (scale > 1) reset();
      else zoomAt(TAP_SCALE, pt.x, pt.y);
    }

    function onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try { container.setPointerCapture(e.pointerId); } catch (_) {}
      moved = false;
      if (pointers.size === 1) downOnImg = img.contains(e.target);
      if (pointers.size === 1) {
        start = { x: e.clientX, y: e.clientY, tx: tx, ty: ty };
        if (scale > 1) container.classList.add('is-panning');
      } else if (pointers.size === 2) {
        var pts = Array.from(pointers.values());
        var mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
        var r = container.getBoundingClientRect();
        start = {
          dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
          scale: scale,
          mid: { x: mid.x - r.left, y: mid.y - r.top },
          tx: tx, ty: ty
        };
      }
      if (scale > 1 || pointers.size === 2) e.preventDefault();
    }

    function onMove(e) {
      if (pointers.has(e.pointerId)) {
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 2 && start && start.dist) {
          var pts = Array.from(pointers.values());
          var dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
          var r = container.getBoundingClientRect();
          var mid = { x: (pts[0].x + pts[1].x) / 2 - r.left, y: (pts[0].y + pts[1].y) / 2 - r.top };
          var target = clamp(start.scale * (dist / start.dist), 1, MAX);
          // zoom about the *starting* midpoint, then pan by midpoint drift
          var ox = img.offsetLeft, oy = img.offsetTop;
          var px = (start.mid.x - ox - start.tx) / start.scale;
          var py = (start.mid.y - oy - start.ty) / start.scale;
          scale = target;
          tx = mid.x - ox - px * scale;
          ty = mid.y - oy - py * scale;
          if (scale === 1) { tx = 0; ty = 0; }
          constrain();
          apply();
          moved = true;
          e.preventDefault();
        } else if (pointers.size === 1 && start && scale > 1) {
          var dx = e.clientX - start.x, dy = e.clientY - start.y;
          if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
          tx = start.tx + dx;
          ty = start.ty + dy;
          constrain();
          apply();
          e.preventDefault();
        }
      } else if (opts.followCursor && scale > 1 && pointers.size === 0 && e.pointerType === 'mouse') {
        // Desktop: pan follows the cursor while zoomed (no drag needed)
        var pt = local(e);
        var cw = container.clientWidth, ch = container.clientHeight;
        var w = img.clientWidth * scale, h = img.clientHeight * scale;
        var b = bounds();
        tx = w > cw ? -(pt.x / cw) * (w - cw) - img.offsetLeft : b.minX;
        ty = h > ch ? -(pt.y / ch) * (h - ch) - img.offsetTop : b.minY;
        constrain();
        apply();
      }
    }

    function onUp(e) {
      if (!pointers.has(e.pointerId)) return;
      pointers.delete(e.pointerId);
      try { container.releasePointerCapture(e.pointerId); } catch (_) {}
      container.classList.remove('is-panning');
      if (pointers.size === 1) {
        var remaining = Array.from(pointers.values())[0];
        start = { x: remaining.x, y: remaining.y, tx: tx, ty: ty };
        return;
      }
      if (pointers.size > 0) return;
      start = null;
      if (moved) return;
      var pt = local(e);
      if (e.pointerType === 'mouse') {
        if (downOnImg) toggleAt(pt);
        return;
      }
      if (!downOnImg) return;
      var now = Date.now();
      if (now - lastTap < 300) {
        lastTap = 0;
        toggleAt(pt);
      } else {
        lastTap = now;
        if (scale === 1 && opts.onTap) {
          window.setTimeout(function () {
            if (lastTap === now) { lastTap = 0; opts.onTap(); }
          }, 300);
        }
      }
    }

    function onWheel(e) {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      var pt = local(e);
      zoomAt(scale * (1 - e.deltaY * 0.01), pt.x, pt.y);
    }

    function onCancel(e) {
      pointers.delete(e.pointerId);
      if (pointers.size === 0) { start = null; container.classList.remove('is-panning'); }
    }

    container.addEventListener('pointerdown', onDown);
    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerup', onUp);
    container.addEventListener('pointercancel', onCancel);
    container.addEventListener('wheel', onWheel, { passive: false });
    container.addEventListener('dragstart', function (e) { e.preventDefault(); });

    return {
      reset: reset,
      isZoomed: function () { return scale > 1; },
      destroy: function () {
        container.removeEventListener('pointerdown', onDown);
        container.removeEventListener('pointermove', onMove);
        container.removeEventListener('pointerup', onUp);
        container.removeEventListener('pointercancel', onCancel);
        container.removeEventListener('wheel', onWheel);
      }
    };
  }

  /* ── Lightbox ────────────────────────────────────────────────────────── */

  function createLightbox(gallery) {
    var dialog = qs(gallery.root, '[data-pmg-lightbox]');
    if (!dialog) return null;

    var track = qs(dialog, '[data-lb-track]');
    var slides = qsa(dialog, '[data-lb-slide]');
    var counter = qs(dialog, '[data-lb-counter]');
    var live = qs(dialog, '[data-lb-live]');
    var prevBtn = qs(dialog, '[data-lb-prev]');
    var nextBtn = qs(dialog, '[data-lb-next]');
    var closeBtn = qs(dialog, '[data-lb-close]');
    var thumbs = createTablist(qs(dialog, '[data-lb-thumbs]'), function (i) { goTo(i, 'thumb'); });
    var current = 0;
    var trigger = null;
    var pushed = false;
    var closingViaPop = false;
    var pinches = {};
    var designMode = gallery.root.hasAttribute('data-design-mode');

    function visible() { return slides.filter(function (s) { return !s.hidden; }); }
    function slideAt(i) { return slides[i]; }

    function hydrate(slide) {
      if (!slide || slide.getAttribute('data-hydrated')) return;
      slide.setAttribute('data-hydrated', '1');
      var pinch = qs(slide, '[data-lb-pinch]');
      var img = qs(slide, 'img[data-src]');
      if (img) {
        if (img.getAttribute('data-srcset')) img.setAttribute('srcset', img.getAttribute('data-srcset'));
        img.setAttribute('src', img.getAttribute('data-src'));
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
        pinches[slide.getAttribute('data-index')] = createPinch(pinch, img, {
          followCursor: isFine(),
          onZoomChange: function (zoomed) { track.classList.toggle('is-locked', zoomed); },
          onTap: null
        });
      }
      var tpl = qs(slide, 'template[data-lb-media]');
      if (tpl) {
        var frag = tpl.content.cloneNode(true);
        pinch.appendChild(frag);
        var video = qs(pinch, 'video');
        if (video) { video.muted = false; }
      }
    }

    function hydrateAround(i) {
      var vis = visible();
      var pos = vis.indexOf(slideAt(i));
      hydrate(slideAt(i));
      if (pos > -1) {
        hydrate(vis[pos + 1]);
        hydrate(vis[pos - 1]);
      }
      idle(function () { vis.forEach(hydrate); }, 3000);
    }

    function pauseMedia(slide) {
      if (!slide) return;
      qsa(slide, 'video').forEach(function (v) { try { v.pause(); } catch (_) {} });
      var iframe = qs(slide, 'iframe');
      if (iframe) {
        // Reset src to stop playback without cross-origin messaging
        var src = iframe.getAttribute('src');
        iframe.setAttribute('src', src);
      }
    }

    function resetPinch(i) {
      var p = pinches[i];
      if (p) p.reset();
    }

    function updateUI() {
      var vis = visible();
      var pos = vis.indexOf(slideAt(current));
      var type = slideAt(current) ? slideAt(current).getAttribute('data-media-type') : 'image';
      var label = (type === 'video' || type === 'external_video') ? 'Video' : (type === 'model' ? '3D model' : 'Image');
      if (counter) counter.textContent = (pos + 1) + ' / ' + vis.length;
      if (live) live.textContent = label + ' ' + (pos + 1) + ' of ' + vis.length;
      if (thumbs) thumbs.setActive(current);
      var single = vis.length < 2;
      if (prevBtn) prevBtn.hidden = single;
      if (nextBtn) nextBtn.hidden = single;
    }

    function goTo(i, source, behavior) {
      var slide = slideAt(i);
      if (!slide || slide.hidden) return;
      var prev = current;
      if (prev !== i) { resetPinch(prev); pauseMedia(slideAt(prev)); }
      current = i;
      hydrateAround(i);
      if (source !== 'scroll') {
        var b = behavior || (reduceMotion() ? 'auto' : 'smooth');
        try { track.scrollTo({ left: slide.offsetLeft, behavior: b }); } catch (_) { track.scrollLeft = slide.offsetLeft; }
      }
      updateUI();
    }

    function step(dir) {
      var vis = visible();
      if (!vis.length) return;
      var pos = vis.indexOf(slideAt(current));
      var next = vis[(pos + dir + vis.length) % vis.length];
      goTo(parseInt(next.getAttribute('data-index'), 10), 'arrow');
    }

    function open(i, triggerEl) {
      if (dialog.open) return;
      trigger = triggerEl || document.activeElement;
      current = i;
      openDialog(dialog);
      qsa(dialog, 'img[loading="lazy"]').forEach(function (im) { im.setAttribute('loading', 'eager'); });
      hydrateAround(i);
      var slide = slideAt(i);
      if (slide) track.scrollLeft = slide.offsetLeft;
      updateUI();
      if (closeBtn) closeBtn.focus();
      gallery.dismissHint();
      if (!designMode && window.history && window.history.pushState) {
        try { window.history.pushState({ pmgLightbox: true }, ''); pushed = true; } catch (_) { pushed = false; }
      }
    }

    function close() { closeDialog(dialog); }

    function onClose() {
      Object.keys(pinches).forEach(function (k) { pinches[k].reset(); });
      slides.forEach(pauseMedia);
      track.classList.remove('is-locked');
      gallery.goTo(current, { source: 'lightbox', behavior: 'auto' });
      if (pushed && !closingViaPop) {
        pushed = false;
        try { window.history.back(); } catch (_) {}
      }
      pushed = false;
      closingViaPop = false;
      if (trigger && typeof trigger.focus === 'function') {
        try { trigger.focus({ preventScroll: true }); } catch (_) { trigger.focus(); }
      }
      trigger = null;
    }

    function onPopstate() {
      if (dialog.open) {
        closingViaPop = true;
        pushed = false;
        close();
      }
    }

    function onKeydown(e) {
      if (!dialog.open) return;
      if (e.target.closest && e.target.closest('[role="tab"]')) return;
      switch (e.key) {
        case 'ArrowRight': e.preventDefault(); step(1); break;
        case 'ArrowLeft': e.preventDefault(); step(-1); break;
        case 'Home': e.preventDefault(); { var v = visible(); if (v[0]) goTo(parseInt(v[0].getAttribute('data-index'), 10)); } break;
        case 'End': e.preventDefault(); { var v2 = visible(); if (v2.length) goTo(parseInt(v2[v2.length - 1].getAttribute('data-index'), 10)); } break;
      }
    }

    var lastPointerType = 'mouse';
    var lastDownTarget = null;
    function onPointerDown(e) { lastPointerType = e.pointerType || 'mouse'; lastDownTarget = e.target; }

    function onTrackClick(e) {
      // Mouse click on the empty area of a slide (not the media) closes.
      // Use the pointerdown target: pointer capture retargets the click itself.
      if (lastPointerType !== 'mouse') return;
      var t = lastDownTarget || e.target;
      var slide = t.closest ? t.closest('[data-lb-slide]') : null;
      if (!slide) return;
      if (t === slide || (t.classList && t.classList.contains('pmg-lightbox__pinch'))) close();
    }

    var onScroll = rafThrottle(function () {
      var i = nearestSlideIndex(track, slides);
      if (i > -1 && i !== current) goTo(i, 'scroll');
    });

    dialog.addEventListener('close', onClose);
    dialog.addEventListener('keydown', onKeydown);
    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('click', onTrackClick);
    track.addEventListener('pointerdown', onPointerDown, { passive: true });
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });
    window.addEventListener('popstate', onPopstate);

    return {
      open: open,
      close: close,
      isOpen: function () { return dialog.open; },
      setHidden: function (index, hidden) {
        var s = slideAt(index);
        if (s) s.hidden = hidden;
        if (thumbs) thumbs.tabs.forEach(function (t) {
          if (parseInt(t.getAttribute('data-index'), 10) === index) t.hidden = hidden;
        });
      },
      refresh: updateUI,
      destroy: function () {
        window.removeEventListener('popstate', onPopstate);
        if (thumbs) thumbs.destroy();
        Object.keys(pinches).forEach(function (k) { pinches[k].destroy(); });
        if (dialog.open) closeDialog(dialog);
      }
    };
  }

  /* ── Gallery ─────────────────────────────────────────────────────────── */

  function initGallery(root) {
    if (!root || instances.has(root)) return instances.get(root);

    var g = {};
    g.root = root;
    g.section = root.closest('.shopify-section') || document;
    g.track = qs(root, '[data-pmg-track]');
    g.viewer = qs(root, '[data-pmg-viewer]');
    g.slides = qsa(root, '[data-pmg-slide]');
    g.counter = qs(root, '[data-pmg-counter]');
    g.dots = qs(root, '[data-pmg-dots]');
    g.live = qs(root, '[data-pmg-live]');
    g.hint = qs(root, '[data-pmg-hint]');
    g.prevBtn = qs(root, '[data-pmg-prev]');
    g.nextBtn = qs(root, '[data-pmg-next]');
    g.expandBtn = qs(root, '[data-pmg-expand]');
    g.layout = root.getAttribute('data-layout') || 'rail';
    g.autoplay = root.getAttribute('data-autoplay') === 'true';
    g.indicator = root.getAttribute('data-indicator') || 'counter';
    g.showCounter = root.getAttribute('data-show-counter') !== 'false';
    g.lightboxEnabled = root.getAttribute('data-lightbox') === 'true';
    g.colourIndex = parseInt(root.getAttribute('data-colour-index'), 10);
    if (isNaN(g.colourIndex)) g.colourIndex = -1;
    g.lastColour = root.getAttribute('data-colour-value') || '';
    g.designMode = root.hasAttribute('data-design-mode');
    try { g.colourMap = JSON.parse(root.getAttribute('data-colour-map') || '{}'); } catch (_) { g.colourMap = {}; }
    g.current = parseInt(root.getAttribute('data-initial'), 10) || 0;
    g.currentMediaId = g.slides[g.current] ? g.slides[g.current].getAttribute('data-media-id') : null;
    g.modelsLoaded = false;
    var listeners = [];

    if (!g.track || !g.slides.length) return null;
    instances.set(root, g);

    function on(target, ev, fn, opts) {
      target.addEventListener(ev, fn, opts);
      listeners.push([target, ev, fn, opts]);
    }

    function visibleSlides() { return g.slides.filter(function (s) { return !s.hidden; }); }
    function slideType(slide) { return slide ? slide.getAttribute('data-media-type') : ''; }
    function typeLabel(slide) {
      var t = slideType(slide);
      return (t === 'video' || t === 'external_video') ? 'Video' : (t === 'model' ? '3D model' : 'Image');
    }

    /* Hint */
    g.dismissHint = function () {
      if (!g.hint) return;
      g.hint.classList.add('is-dismissed');
      g.hint.classList.remove('is-visible');
      if (!g.designMode) { try { window.sessionStorage.setItem(HINT_KEY, '1'); } catch (_) {} }
    };

    function setupHint() {
      if (!g.hint) return;
      var seen = false;
      if (!g.designMode) { try { seen = window.sessionStorage.getItem(HINT_KEY) === '1'; } catch (_) {} }
      var zoom = root.getAttribute('data-zoom');
      var text = '';
      if (isMobile() || !isFine()) {
        text = g.lightboxEnabled ? 'Tap to zoom' : '';
      } else if (zoom === 'hover') {
        text = 'Hover to zoom';
      } else if (zoom === 'click') {
        text = 'Click to zoom';
      } else if (g.lightboxEnabled) {
        text = 'Click to enlarge';
      }
      g.hint.textContent = text;
      if (seen || !text) { g.hint.classList.add('is-dismissed'); return; }
      if (isMobile()) {
        g.hint.classList.add('is-visible');
        window.setTimeout(function () { g.hint.classList.remove('is-visible'); }, 3500);
      }
    }

    /* Media control */
    function pauseMedia(slide) {
      if (!slide) return;
      qsa(slide, 'video').forEach(function (v) { try { v.pause(); } catch (_) {} });
      var ext = qs(slide, '[data-pmg-external]');
      if (ext && ext.classList.contains('is-playing')) {
        var embed = qs(ext, '.pmg__embed');
        if (embed) embed.remove();
        ext.classList.remove('is-playing');
      }
      var mv = qs(slide, 'model-viewer');
      if (mv && typeof mv.pause === 'function') { try { mv.pause(); } catch (_) {} }
    }

    function playMedia(slide) {
      if (!slide) return;
      var video = qs(slide, 'video[data-autoplay]');
      if (video && g.autoplay && !reduceMotion()) {
        video.muted = true;
        var p = video.play();
        if (p && p.catch) p.catch(function () { video.controls = true; });
      } else if (video && reduceMotion()) {
        video.controls = true;
      }
      if (slideType(slide) === 'model') loadModels();
    }

    function playExternal(ext) {
      var tpl = qs(ext, 'template[data-pmg-embed]');
      if (!tpl || ext.classList.contains('is-playing')) return;
      var embed = document.createElement('div');
      embed.className = 'pmg__embed';
      embed.appendChild(tpl.content.cloneNode(true));
      var iframe = qs(embed, 'iframe');
      if (iframe) {
        try {
          var url = new URL(iframe.getAttribute('src'), window.location.href);
          url.searchParams.set('autoplay', '1');
          if (/youtube/.test(url.hostname)) url.searchParams.set('playsinline', '1');
          iframe.setAttribute('src', url.toString());
        } catch (_) {}
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      }
      ext.appendChild(embed);
      ext.classList.add('is-playing');
    }

    function loadModels() {
      if (g.modelsLoaded) return;
      g.modelsLoaded = true;
      var json = qs(root, '[data-pmg-models]');
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: function () {
            qsa(root, 'model-viewer').forEach(function (mv) {
              if (mv._pmgUI) return;
              try { mv._pmgUI = new window.Shopify.ModelViewerUI(mv); } catch (_) {}
            });
          }
        },
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: function () {
            if (!window.ShopifyXR || !json) return;
            try {
              window.ShopifyXR.addModels(JSON.parse(json.textContent));
              window.ShopifyXR.setupXRElements();
            } catch (_) {}
          }
        }
      ]);
    }

    /* Indicators */
    function updateIndicators() {
      var vis = visibleSlides();
      var slide = g.slides[g.current];
      var pos = vis.indexOf(slide);
      var total = vis.length;
      var showCounter = g.showCounter && total > 1 && !(isMobile() && g.indicator === 'dots' && total <= 8);
      if (g.counter) {
        g.counter.textContent = (pos + 1) + ' / ' + total;
        g.counter.hidden = !showCounter;
      }
      if (g.dots) {
        var useDots = g.indicator === 'dots' && total > 1 && total <= 8;
        g.dots.hidden = !useDots;
        if (useDots) {
          if (g.dots.childElementCount !== total) {
            g.dots.innerHTML = '';
            for (var i = 0; i < total; i++) {
              var d = document.createElement('span');
              d.className = 'pmg__dot';
              g.dots.appendChild(d);
            }
          }
          qsa(g.dots, '.pmg__dot').forEach(function (d, i) { d.classList.toggle('is-active', i === pos); });
        }
      }
      if (g.prevBtn) g.prevBtn.hidden = total < 2;
      if (g.nextBtn) g.nextBtn.hidden = total < 2;
      if (g.live && slide) g.live.textContent = typeLabel(slide) + ' ' + (pos + 1) + ' of ' + total;
      positionBadges();
    }

    function prefetchNeighbours() {
      var vis = visibleSlides();
      var pos = vis.indexOf(g.slides[g.current]);
      [vis[pos + 1], vis[pos - 1]].forEach(function (s) {
        if (!s) return;
        var img = qs(s, 'img[loading="lazy"]');
        if (img) img.setAttribute('loading', 'eager');
      });
    }

    /* Badge inset — badges are viewer-anchored so they survive the crossfade,
       but in natural ratio the track is as tall as the tallest image and a
       shorter photo sits centred beneath a white bar, leaving a corner-pinned
       badge floating off the photo. Measure the active photo's top gap and
       hand it to CSS via --pmg-media-top (see product-gallery.css). */
    g.badges = qsa(g.viewer, '.product-pill, .product-badge, .product-flag').filter(function (b) {
      return b.parentElement === g.viewer;
    });

    function positionBadges() {
      if (!g.badges.length || !g.viewer) return;
      var slide = g.layout === 'stacked' ? visibleSlides()[0] : g.slides[g.current];
      if (!slide || slide.hidden) return;
      var target = qs(slide, '.pmg__img, .pmg__video, .pmg__model, .pmg__placeholder') || qs(slide, '.pmg__media') || slide;
      var vRect = g.viewer.getBoundingClientRect();
      var tRect = target.getBoundingClientRect();
      if (!tRect.height || !vRect.height) return;
      var top = Math.max(0, Math.round(tRect.top - vRect.top));
      g.viewer.style.setProperty('--pmg-media-top', top + 'px');
    }

    /* Core navigation */
    g.goTo = function (index, opts) {
      opts = opts || {};
      var slide = g.slides[index];
      if (!slide) return;
      if (slide.hidden) {
        slide.hidden = false;
        if (g.thumbs) g.thumbs.tabs.forEach(function (t) {
          if (parseInt(t.getAttribute('data-index'), 10) === index) t.hidden = false;
        });
        if (g.lightbox) g.lightbox.setHidden(index, false);
      }
      var prevSlide = g.slides[g.current];
      var changed = index !== g.current;
      g.current = index;
      g.currentMediaId = slide.getAttribute('data-media-id');

      g.slides.forEach(function (s) {
        s.classList.toggle('is-active', s === slide);
      });
      if (g.thumbs) g.thumbs.setActive(index, opts.behavior);

      if (opts.source !== 'scroll') {
        var behavior = opts.behavior || (reduceMotion() ? 'auto' : 'smooth');
        if (isMobile()) {
          try { g.track.scrollTo({ left: slide.offsetLeft, behavior: behavior }); } catch (_) { g.track.scrollLeft = slide.offsetLeft; }
        } else if (g.layout === 'stacked' && (opts.source === 'variant' || opts.source === 'lightbox')) {
          try { slide.scrollIntoView({ behavior: behavior, block: 'nearest' }); } catch (_) {}
        }
      }

      if (changed) {
        if (g.zoom) g.zoom.deactivate();
        pauseMedia(prevSlide);
      }
      playMedia(slide);
      updateIndicators();
      prefetchNeighbours();
      if (g.zoom && !isMobile() && isFine() && slideType(slide) === 'image') {
        idle(function () { if (g.current === index) g.zoom.warm(slide).catch(function () {}); }, 2000);
      }
    };

    g.goToMediaId = function (id, opts) {
      for (var i = 0; i < g.slides.length; i++) {
        if (g.slides[i].getAttribute('data-media-id') === String(id)) { g.goTo(i, opts); return true; }
      }
      return false;
    };

    g.step = function (dir) {
      var vis = visibleSlides();
      if (vis.length < 2) return;
      var pos = vis.indexOf(g.slides[g.current]);
      var next = vis[(pos + dir + vis.length) % vis.length];
      g.goTo(g.slides.indexOf(next), { source: 'arrow' });
    };

    /* Colour filter */
    g.applyColourFilter = function (value) {
      if (g.colourIndex < 0) return;
      var handle = g.colourMap[value] || handleize(value);
      var matched = 0;
      g.slides.forEach(function (s) {
        var cols = (s.getAttribute('data-colours') || '').split(' ').filter(Boolean);
        if (handle && cols.indexOf(handle) > -1) matched++;
      });
      var failOpen = !handle || matched === 0;
      g.slides.forEach(function (s, i) {
        var cols = (s.getAttribute('data-colours') || '').split(' ').filter(Boolean);
        var hidden = !failOpen && cols.length > 0 && cols.indexOf(handle) === -1;
        s.hidden = hidden;
        if (g.thumbs) g.thumbs.tabs.forEach(function (t) {
          if (parseInt(t.getAttribute('data-index'), 10) === i) t.hidden = hidden;
        });
        if (g.lightbox) g.lightbox.setHidden(i, hidden);
      });
      // Stacked "lead" slide = first visible
      var vis = visibleSlides();
      g.slides.forEach(function (s) { s.classList.toggle('pmg__slide--lead', s === vis[0]); });
      if (g.slides[g.current] && g.slides[g.current].hidden && vis[0]) {
        g.goTo(g.slides.indexOf(vis[0]), { source: 'variant', behavior: 'auto' });
      } else {
        updateIndicators();
      }
      if (g.lightbox) g.lightbox.refresh();
      if (g.live && value && !failOpen) {
        g.live.textContent = 'Showing ' + vis.length + ' ' + (vis.length === 1 ? 'image' : 'images') + ' for ' + value;
      }
    };

    /* Variant sync */
    function onVariantChange(e) {
      var detail = e.detail || {};
      var variant = detail.variant;
      var selected = detail.selected || [];
      var colourChanged = false;
      if (g.colourIndex > -1) {
        var value = selected[g.colourIndex];
        if (value != null && value !== g.lastColour) {
          g.lastColour = value;
          colourChanged = true;
          g.applyColourFilter(value);
        }
      }
      if (!variant || !variant.featured_media) return;
      var mediaId = String(variant.featured_media.id);
      var shouldJump = g.colourIndex > -1 ? colourChanged : mediaId !== g.currentMediaId;
      if (shouldJump && mediaId !== g.currentMediaId) {
        g.goToMediaId(mediaId, { source: 'variant', behavior: isMobile() ? 'auto' : undefined });
      }
    }

    /* Bindings */
    g.thumbs = createTablist(qs(root, '[data-pmg-thumbs]'), function (i) { g.goTo(i, { source: 'thumb' }); });

    if (g.prevBtn) on(g.prevBtn, 'click', function () { g.step(-1); });
    if (g.nextBtn) on(g.nextBtn, 'click', function () { g.step(1); });

    var onTrackScroll = rafThrottle(function () {
      if (!isMobile()) return;
      var i = nearestSlideIndex(g.track, g.slides);
      if (i > -1 && i !== g.current) g.goTo(i, { source: 'scroll' });
    });
    on(g.track, 'scroll', onTrackScroll, { passive: true });

    on(g.track, 'keydown', function (e) {
      if (e.target !== g.track) return;
      switch (e.key) {
        case 'ArrowRight': e.preventDefault(); g.step(1); break;
        case 'ArrowLeft': e.preventDefault(); g.step(-1); break;
        case 'Enter':
        case ' ':
          if (g.lightbox) { e.preventDefault(); g.lightbox.open(g.current, g.track); }
          break;
        case 'Escape':
          if (g.zoom) g.zoom.deactivate();
          break;
      }
    });

    // External video play buttons
    on(g.track, 'click', function (e) {
      var play = e.target.closest('[data-pmg-play]');
      if (!play) return;
      var ext = play.closest('[data-pmg-external]');
      if (ext) { e.preventDefault(); playExternal(ext); }
    });

    // Zoom (desktop) — created before the lightbox click handler so click mode wins
    if (root.getAttribute('data-zoom') !== 'off') g.zoom = createZoom(g);

    // Lightbox
    if (g.lightboxEnabled) g.lightbox = createLightbox(g);
    if (g.expandBtn && g.lightbox) on(g.expandBtn, 'click', function () { g.lightbox.open(g.current, g.expandBtn); });

    // Tap (touch) or click (zoom off) on an image slide opens the lightbox
    on(g.track, 'click', function (e) {
      if (!g.lightbox) return;
      var slide = e.target.closest('[data-pmg-slide]');
      if (!slide || slideType(slide) !== 'image') return;
      if (!e.target.closest('.pmg__media')) return;
      var zoomMode = root.getAttribute('data-zoom');
      var wantsLightbox = isMobile() || !isFine() || zoomMode === 'off' || zoomMode === 'hover';
      if (!wantsLightbox) return;
      e.preventDefault();
      g.lightbox.open(g.slides.indexOf(slide), g.track);
    });

    // Autoplay hosted video when on screen (mobile track / active slide)
    if ('IntersectionObserver' in window) {
      g.videoIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var slide = entry.target;
          var video = qs(slide, 'video[data-autoplay]');
          if (!video) return;
          var active = slide.classList.contains('is-active') || isMobile();
          if (entry.isIntersecting && active && g.autoplay && !reduceMotion()) {
            video.muted = true;
            var p = video.play(); if (p && p.catch) p.catch(function () {});
          } else {
            try { video.pause(); } catch (_) {}
          }
        });
      }, { threshold: 0.5 });
      g.slides.forEach(function (s) { if (qs(s, 'video[data-autoplay]')) g.videoIO.observe(s); });
    }

    // Load model-viewer immediately if the initial slide is a model
    if (slideType(g.slides[g.current]) === 'model') loadModels();

    // Variant changes come from the section script
    on(g.section, 'variant:change', onVariantChange);

    // Badge inset tracks the photo box — natural-ratio heights settle as
    // images load, and the viewer resizes with the viewport / colour filter.
    if (g.badges.length) {
      var repositionBadges = rafThrottle(positionBadges);
      g.slides.forEach(function (s) {
        var img = qs(s, 'img');
        if (img && !img.complete) on(img, 'load', repositionBadges);
      });
      if ('ResizeObserver' in window) {
        g.badgeRO = new ResizeObserver(repositionBadges);
        g.badgeRO.observe(g.viewer);
      } else {
        on(window, 'resize', repositionBadges);
      }
    }

    // Layout crossing (resize / orientation)
    function onLayoutChange() {
      g.goTo(g.current, { source: 'layout', behavior: 'auto' });
      setupHint();
    }
    if (mqDesktop.addEventListener) mqDesktop.addEventListener('change', onLayoutChange);
    else mqDesktop.addListener(onLayoutChange);

    g.destroy = function () {
      listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
      if (g.thumbs) g.thumbs.destroy();
      if (g.zoom) g.zoom.destroy();
      if (g.lightbox) g.lightbox.destroy();
      if (g.videoIO) g.videoIO.disconnect();
      if (g.badgeRO) g.badgeRO.disconnect();
      if (mqDesktop.removeEventListener) mqDesktop.removeEventListener('change', onLayoutChange);
      else mqDesktop.removeListener(onLayoutChange);
      instances.delete(root);
    };

    /* Initial state */
    setupHint();
    updateIndicators();
    if (isMobile() && g.current > 0) {
      var s0 = g.slides[g.current];
      g.track.scrollLeft = s0.offsetLeft;
    }
    playMedia(g.slides[g.current]);
    prefetchNeighbours();

    return g;
  }

  /* ── Boot ────────────────────────────────────────────────────────────── */

  function initAll(scope) {
    qsa(scope || document, '[data-product-gallery]').forEach(initGallery);
  }

  function destroyIn(scope) {
    qsa(scope || document, '[data-product-gallery]').forEach(function (root) {
      var g = instances.get(root);
      if (g && g.destroy) g.destroy();
    });
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroyIn(e.target); });

  window.McCallsProductGallery = { init: initGallery, instances: instances };
})();
